import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector, shallowEqual } from 'react-redux'
import { useModal, Dialog, Text } from '@leroy-merlin-br/backyard-react-ui'

import emitter from 'garden/src/js/utils/emitter'

import getUserContext from 'scripts/utils/getUserContext'
import { fetchCartShipping, removeProductFromCart } from 'scripts/api/cart'

import { getRegionNameRef } from 'reactSite/Location/Hooks'
import {
  changeRegionName,
  dataLayer,
  handleSpinner
} from 'reactSite/Cart/services'
import PostalCode from 'reactSite/Cart/components/PostalCode'
import Section from 'reactSite/Cart/components/Section'
import Shell from 'reactSite/Cart/components/Shell'
import Summary from 'reactSite/Cart/components/Summary'
import EmptyCart from 'reactSite/Cart/components/EmptyCart'
import Error from 'reactSite/Cart/components/Error'
import { ERRORS } from 'reactSite/Cart/services/constants'

import { tracker } from 'reactUtils/app'

import { Actions } from 'checkout/redux/modules/crossServices'
import { Actions as CartActions } from 'checkout/redux/modules/cart'

import * as S from './styled'
import { getProductsServices } from './util'

const setRegionOnApp = region => {
  window.ReactNativeWebView &&
    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        type: 'updateRegion',
        data: {
          regionName: region.name,
          region: region.slug
        }
      })
    )
}

const Cart = ({ regionInit, regionName }) => {
  const [cart, setCart] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [shouldFetchShipments, setShouldFetchShipments] = useState(true)
  const { setModal } = useModal()

  const dispatch = useDispatch()

  const region = useSelector(state => state.cart.region, shallowEqual)

  const onRemove = product => {
    const removeItem = async callback => {
      dataLayer.removeProduct(product)
      setIsLoading(true)
      await removeProductFromCart(product.id)
      emitter.emit('cart:product-removed')

      if (product.services.length) {
        const {
          serviceId,
          name,
          price,
          quantity,
          serviceBankName
        } = product.services[0]

        dataLayer.removeService({
          regionId: region.id,
          serviceId,
          price,
          quantity,
          serviceName: name,
          category: serviceBankName
        })
      }

      fetchPriceShipping(cart.zipCode)
      return callback()
    }

    setModal({
      appElement: 'body',
      children: ({ onClose }) => (
        <Dialog
          data-cy="cart-remove-product-dialog"
          onClose={onClose}
          title="Atenção!"
          actions={[
            {
              color: 'red',
              content: 'Excluir',
              onClick: () => removeItem(onClose)
            },
            {
              content: 'Manter na lista',
              onClick: onClose
            }
          ]}
        >
          <Text noMargin>Deseja mesmo excluir esse item da lista?</Text>
        </Dialog>
      )
    })
  }

  const hasCEPErrors = errors =>
    errors && ERRORS.CEP.some(error => errors[error])

  const getCEPErrors = errors => ERRORS.CEP.find(error => errors[error])

  const openPriceShippingErrorDialog = useCallback((message, needReload) => {
    function handleClose (onClose) {
      if (needReload) {
        location.replace('/carrinho')
      }
      onClose()
    }

    setModal({
      appElement: 'body',
      children: ({ onClose }) => (
        <Dialog
          data-cy="fetch-price-shipping-error-dialog"
          onClose={() => handleClose(onClose)}
          title="Ops!"
          actions={[
            {
              content: 'Ok',
              onClick: () => {
                handleClose(onClose)
              }
            }
          ]}
        >
          <Text noMargin>{message}</Text>
        </Dialog>
      )
    })
  }, [setModal])

  useEffect(() => {
    if (!hasCEPErrors(cart.errors)) {
      return
    }

    setModal({
      appElement: 'body',
      children: ({ onClose }) => (
        <Dialog
          data-cy="cart-cep-dialog"
          onClose={onClose}
          title="Atenção!"
          actions={[
            {
              content: 'Confirmar',
              onClick: onClose
            }
          ]}
        >
          <Text>{cart.errors[getCEPErrors(cart.errors)][0]}</Text>
        </Dialog>
      )
    })
  }, [cart, setModal])

  const updateCartWhenServiceWasAdded = useCallback(cart => {
    if (cart) {
      setCart(cart)
    }
  }, [])

  const setServicesPerItem = useCallback(
    async cart => {
      const shipments = await getProductsServices(cart, region.id)
      dispatch(Actions.clearServices())

      shipments.forEach(shipment => {
        dispatch(Actions.updateList(shipment))
      })
    },
    [dispatch, region]
  )

  const fetchPriceShipping = useCallback(
    async (postalCode, zipCodeToDL) => {
      const useSpinner = cart !== null
      handleSpinner({ useSpinner, action: 'show' })

      try {
        const response = await fetchCartShipping(postalCode)
        setServicesPerItem(response)

        setCart(response)
        setIsLoading(false)
        handleSpinner({ useSpinner })

        if (response.region) {
          dispatch(CartActions.setRegion(response.region))
          const myRef = getRegionNameRef()
          changeRegionName({ ref: myRef, regionName: response.region.name })
        }

        if (zipCodeToDL) {
          dataLayer.freightCostsCart(response.shippings)
        }

        tracker.freightCosts(response)
      } catch ({
        response: {
          data: { message, needReload, region }
        }
      }) {
        if (cart.length) setCart({})

        setIsLoading(false)

        if (region) {
          setRegionOnApp(region)
        }

        handleSpinner({ useSpinner })

        openPriceShippingErrorDialog(message, needReload)
      }
    },
    [cart, dispatch, setServicesPerItem, openPriceShippingErrorDialog]
  )

  const updateShipment = data => {
    emitter.emit('cart:products-quantity', data)
    setShouldFetchShipments(true)
  }

  useEffect(() => {
    let callShipmentsAPI
    if (shouldFetchShipments) {
      callShipmentsAPI = setTimeout(async () => {
        const zipCode = cart.zipCode || ''
        await fetchPriceShipping(zipCode)

        setShouldFetchShipments(false)
      }, 1000)
    }

    return () => {
      clearTimeout(callShipmentsAPI)
    }
  }, [shouldFetchShipments, cart, fetchPriceShipping])

  useEffect(() => {
    emitter.on('cart:products-replace', () => {
      setShouldFetchShipments(true)
    })
  }, [])

  useEffect(() => {
    emitter.on('cart:service-change', cart => {
      updateCartWhenServiceWasAdded(cart)
    })
  }, [updateCartWhenServiceWasAdded])

  useEffect(() => {
    if (regionInit) {
      dispatch(CartActions.setRegion({ id: regionInit, name: regionName }))
    }
  }, [regionInit, regionName, dispatch])

  if (isLoading) {
    return <Shell />
  }

  const {
    subTotal,
    discountTotal,
    freightCost,
    servicesSubTotal,
    total,
    zipCode,
    shippings
  } = cart || {}

  const hasCart = cart !== null && !!shippings?.length

  return (
    <S.Wrapper>
      <S.CartSection>
        {cart.errors && <Error errors={cart.errors} />}

        {hasCart && shippings[0]?.type !== 'pickupInStore' && (
          <PostalCode
            errors={cart.errors}
            zipCode={zipCode}
            fetchPriceShipping={fetchPriceShipping}
          />
        )}

        {shippings?.map(({ type, shipments }) => {
          return shipments?.map(({ items, modalities }, i) => (
            <Section
              key={`${type}-${i}`}
              freightCost={freightCost}
              modalities={modalities}
              onRemove={onRemove}
              products={items}
              type={type}
              updateProduct={updateShipment}
              services={cart.services}
              regionId={region.id}
            />
          ))
        })}

        {!hasCart && <EmptyCart />}
      </S.CartSection>

      {hasCart && (
        <div style={{ flex: 1 }}>
          <Summary
            cart={cart}
            services={cart.services}
            context={getUserContext()}
            discount={discountTotal}
            canBePurchased={cart.canBePurchased}
            freight={freightCost}
            subtotal={subTotal}
            servicesSubTotal={servicesSubTotal}
            total={total}
            regionId={region.id}
          />
        </div>
      )}
    </S.Wrapper>
  )
}

export default Cart
